import React, { memo, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';

import PropTypes from 'prop-types';
import { defaultRanges } from './constants';
import { useFilters } from '../../../providers/Filters/useFilters';
import { validFiltersDate } from '../../../utils/filters';

import { Dropdown } from '../../Dropdown';
import { ButtonFilter } from '../../Buttons';
import { FilterTitle } from '../index';
import Body from './Body';

const params = ['period_code', 'date_from', 'date_to'];

const FilterCreated = ({ period_code, date_from, date_to, ranges, filterMethod }) => {
  const { filters, onAddFiltersParams, onClearFilters, onAddDefaultClear, onChangeFilter } = useFilters();
  const [defaultRange] = useState(ranges?.length ? ranges[0] : null)
  const [dataRanges] = useState(ranges || []);
  const [filterSelected, setSelectedFilter] = useState({
    period_code: period_code || null,
    date_from: null,
    date_to: null,
  });

  const isDefaultFilter = useMemo(() => {
    if (!defaultRange) {
      return false;
    }
    const defaultObj = {
      period_code: defaultRange.code,
      date_from: defaultRange.date_from,
      date_to: defaultRange.date_to,
    }
    return isEqual(filterSelected, defaultObj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelected, ranges])

  const activeText = useMemo(() => {
    const { period_code, date_from, date_to } = filterSelected;
    if (!period_code) return null;

    const rangeSelected = dataRanges.find((item) => item.code === period_code);
    if (!rangeSelected) return null;

    return rangeSelected.code !== 'custom_range'
      ? rangeSelected.title
      : [date_from, date_to].filter(Boolean).join(' - ') || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelected]);

  const onClearFilter = (e) => {
    e.preventDefault();
    onClearFilters({
      withDeleteParams: params,
    })
  }

  const onChangeSelectedFilter = (filter) => {
    if (filter) {
      if (defaultRange.code === filter.period_code) {
        onClearFilters({
          withDeleteParams: params,
        })
      } else {
        setSelectedFilter(filter);
        onChangeFilter({
          [filterMethod]: filter,
        })
      }
    }
  }

  useEffect(() => {
    if (defaultRange) {
      const defaultObj = {
        period_code: defaultRange.code,
        date_from: defaultRange.date_from,
        date_to: defaultRange.date_to,
      }

      const currentFilter = {
        period_code,
        date_from,
        date_to,
      }

      if (!isEqual(defaultObj, currentFilter)) {
        onAddDefaultClear(defaultObj)
        setSelectedFilter(defaultObj)
      }

      setSelectedFilter(currentFilter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period_code, date_from, date_to]);

  useEffect(() => {
    if (filters.query?.period_code) {
      setSelectedFilter((prevState) => ({
        period_code: filters.query.period_code || prevState.period_code,
        date_from: validFiltersDate(filters.query.date_from) ? filters.query.date_from : null,
        date_to: validFiltersDate(filters.query.date_to) ? filters.query.date_to : null,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query.period_code]);

  useEffect(() => {
    onAddFiltersParams(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dropdown.Root>
      <Dropdown.Trigger>
        <ButtonFilter
          name="Created"
          activeText={activeText}
          onClear={!isDefaultFilter ? onClearFilter : null}
        />
      </Dropdown.Trigger>
      <Dropdown.Container
        position="bottom"
        width={240}
      >
        <Dropdown.Header p={16}>
          <FilterTitle>Filter by Created</FilterTitle>
        </Dropdown.Header>
        <Body
          ranges={dataRanges}
          filter={filterSelected}
          onChangeFilter={onChangeSelectedFilter}
        />
      </Dropdown.Container>
    </Dropdown.Root>
  );
}

FilterCreated.defaultProps = {
  ranges: defaultRanges,
  period_code: 'custom_range',
  date_from: null,
  date_to: null,
  filterMethod: 'query',
};

FilterCreated.propTypes = {
  period_code: PropTypes.string,
  date_from: PropTypes.string,
  date_to: PropTypes.string,
  ranges: PropTypes.array,
  filterMethod: PropTypes.string,
};

export default memo(FilterCreated);
